import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@/providers/ThemeProvider';

/**
 * "Continue with Google", drawn by Google Identity Services itself (Google's branding rules
 * require its own button). Clicking opens Google's popup; on success GIS calls back with an ID
 * token (`credential`), which the API verifies and exchanges for a session.
 *
 * Switched by the BUILD-time REACT_APP_GOOGLE_CLIENT_ID: unset renders nothing at all, so the
 * pages look exactly as before until a client id is configured.
 */
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() || '';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

export const isGoogleSignInEnabled = CLIENT_ID !== '';

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleAccountsId {
  initialize(config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    ux_mode?: 'popup' | 'redirect';
    context?: 'signin' | 'signup' | 'use';
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      logo_alignment?: 'left' | 'center';
      width?: number;
    }
  ): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

// One script tag per page load, shared by every button; a failed load is retried next mount.
let gisLoader: Promise<void> | null = null;

function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  gisLoader ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gisLoader = null;
      reject(new Error('Google Identity Services failed to load'));
    };
    document.head.appendChild(script);
  });
  return gisLoader;
}

interface GoogleSignInButtonProps {
  /** Only changes Google's popup wording ("Sign in to…" vs "Sign up for…"). */
  context: 'signin' | 'signup';
  onCredential: (credential: string) => void;
  disabled?: boolean;
}

export function GoogleSignInButton({ context, onCredential, disabled }: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [unavailable, setUnavailable] = useState(false);
  const { resolvedTheme } = useTheme();

  // GIS keeps the callback it was initialised with, so route the handler and the disabled flag
  // through refs to always read the latest values without re-rendering Google's button.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;

    loadGoogleIdentityServices().then(
      () => {
        const container = containerRef.current;
        if (cancelled || !container || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (response) => {
            // A popup can finish while another sign-in is already in flight; drop that credential
            // rather than submit a second one.
            if (response.credential && !disabledRef.current) {
              onCredentialRef.current(response.credential);
            }
          },
          ux_mode: 'popup',
          context,
        });
        // GIS takes a fixed pixel width (200–400) and never reflows, so measure once. Clearing
        // first keeps a remount (React StrictMode, route change) from stacking two buttons.
        const width = Math.min(400, Math.max(200, Math.floor(container.getBoundingClientRect().width)));
        container.replaceChildren();
        window.google.accounts.id.renderButton(container, {
          theme: resolvedTheme === 'dark' ? 'filled_black' : 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width,
        });
      },
      () => {
        if (!cancelled) setUnavailable(true);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [context, resolvedTheme]);

  if (!CLIENT_ID) return null;
  if (unavailable) {
    return (
      <p className="text-center text-sm text-slate-500 dark:text-slate-400">
        Google sign-in is unavailable right now. Use your email instead.
      </p>
    );
  }
  return (
    <div
      ref={containerRef}
      aria-busy={disabled}
      className={`flex min-h-[44px] justify-center ${disabled ? 'pointer-events-none opacity-60' : ''}`}
    />
  );
}

interface GoogleSignInSectionProps extends GoogleSignInButtonProps {
  /** Rendered under the button, e.g. the terms notice on sign-up. */
  footnote?: React.ReactNode;
}

/** The Google button plus the "or" divider that separates it from the email form. */
export function GoogleSignInSection({ footnote, ...buttonProps }: GoogleSignInSectionProps) {
  if (!isGoogleSignInEnabled) return null;
  return (
    <div className="mb-3 space-y-2.5">
      <GoogleSignInButton {...buttonProps} />
      {footnote}
      <div className="relative pt-0.5">
        <div className="absolute inset-0 flex items-center pt-0.5">
          <div className="w-full border-t border-slate-200/80 dark:border-slate-800" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white dark:bg-slate-900 px-2 text-slate-400 dark:text-slate-500 font-medium text-[11px]">or use your email</span>
        </div>
      </div>
    </div>
  );
}
